import { Collapsible } from '@base-ui/react/collapsible';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, PropsWithChildren, ReactNode } from 'react';
import ConditionalWrapper from '../ConditionalWrapper/ConditionalWrapper';
import Tooltip from '../Tooltip/Tooltip';

/**
 * A single collapsible section with a clickable header.
 *
 * ## When to use
 *
 * - Hiding secondary detail behind a header the user can expand on demand,
 *   e.g. a financial breakdown, a card's extra fields.
 *
 * ## When to choose another component
 *
 * - **Navigation groups** — use `NavList.Group`. `Accordion` is for
 *   collapsing content, not for a list of links.
 * - **Several sections where only one should be open at a time** — `Accordion`
 *   has no group primitive; each instance is independent. Compose your own
 *   open-state management across a list of `Accordion`s if that's needed.
 *
 * ## Controlled vs uncontrolled
 *
 * Uncontrolled by default — `defaultOpen` seeds the initial state and the
 * Accordion manages the rest itself. Pass `open` instead when the initial
 * state depends on data that can arrive after mount (e.g. a count from a
 * query): unlike `defaultOpen`, `open` is read on every render, so there is no
 * need to force a remount to make a later-arriving value take effect. Pair
 * `open` with `onOpenChange` if the user should still be able to toggle it.
 */
export type AccordionProps = {
  title: string | ReactNode;
  titleTooltip?: string;
  chevronPosition?: 'left' | 'right';
  /**
   * Controlled open state. Omit to let the Accordion manage its own state,
   * seeded by `defaultOpen`.
   */
  open?: boolean;
  /**
   * Initial open state for an uncontrolled Accordion. Has no effect once
   * `open` is passed.
   */
  defaultOpen?: boolean;
  panelClassName?: string;
  disabled?: boolean;
  summary?: ReactNode;
  /**
   * Header content outside the trigger — a link or button that must not sit
   * inside the title control.
   */
  actions?: ReactNode;
  /** Called with the next open state whenever the header is toggled. */
  onOpenChange?: (open: boolean) => void;
};

const Accordion: FC<PropsWithChildren<AccordionProps>> = (props) => {
  const {
    title,
    titleTooltip,
    children,
    chevronPosition = 'right',
    open,
    defaultOpen,
    panelClassName,
    disabled = false,
    summary,
    actions,
    onOpenChange,
  } = props;

  const titleNode =
    typeof title === 'string' ? <span className="h4">{title}</span> : title;

  return (
    <Collapsible.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange && ((isOpen) => onOpenChange(isOpen))}
      disabled={disabled}
      render={(_rootProps, state) => {
        const chevronNode = !disabled && (
          <UntitledIcon
            name={state.open ? 'chevron-up' : 'chevron-down'}
            className="h-5 w-5 shrink-0"
          />
        );

        const summaryNode = !state.open && summary;

        return (
          <>
            <div className={actions ? 'flex items-center gap-3' : undefined}>
              <ConditionalWrapper
                condition={!!titleTooltip}
                Wrapper={Tooltip}
                wrapperProps={{
                  className: '!block',
                  tooltipContent: titleTooltip,
                }}
              >
                <Collapsible.Trigger
                  className={`typography:prose bg-transparent p-0 flex gap-2 cursor-pointer text-left items-center justify-between ${
                    actions ? 'min-w-0 grow' : 'w-full'
                  }`}
                >
                  <span className="flex items-center gap-2 grow min-w-0">
                    {chevronPosition === 'left' && chevronNode}
                    {titleNode}
                    {chevronPosition === 'right' && summaryNode}
                  </span>

                  {chevronPosition === 'right' ? chevronNode : summaryNode}
                </Collapsible.Trigger>
              </ConditionalWrapper>
              {actions ? <div className="shrink-0">{actions}</div> : null}
            </div>

            {children && (
              <Collapsible.Panel className={panelClassName}>
                {children}
              </Collapsible.Panel>
            )}
          </>
        );
      }}
    />
  );
};

export default Accordion;
